import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { CutListItem } from "./CutListItem";
import { fuzzyScore } from "./PalletTypesSidebar";
import { X } from "lucide-react";

// Per-cut-list completed components history (moved out of Settings)
export function CutComponentsHistory({ open, onOpenChange, items, listName, onRestore, onDelete }) {
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const noop = () => {};
  const fmtEst = (minutes) => { if (minutes < 1) return `${Math.round(minutes * 60)} seconds`; if (minutes >= 60) { const h = Math.floor(minutes / 60); const m = Math.round(minutes % 60); return m > 0 ? `${h}h ${m}m` : `${h}h`; } return `${minutes.toFixed(1)} min`; };
  const getElapsed = (i) => i.stopwatch_elapsed || 0;
  const fmtSw = (s) => { const secs = Math.max(0, Math.floor(s || 0)); const h = Math.floor(secs/3600); const m = Math.floor((secs%3600)/60); const sc = secs%60; return h > 0 ? `${h}:${String(m).padStart(2,'0')}:${String(sc).padStart(2,'0')}` : `${String(m).padStart(2,'0')}:${String(sc).padStart(2,'0')}`; };
  const dateStrings = (ds) => { if (!ds) return ''; const d = new Date(String(ds).includes('T') ? ds : ds + 'T12:00:00'); if (isNaN(d.getTime())) return String(ds); return [String(ds), d.toLocaleDateString(), d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }), d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`, String(d.getFullYear())].join(' '); };
  const haystack = (i) => [
    i.name || '', i.pallet_type_name || '', i.notes || '', i.extras || '',
    String(i.total || ''), String(i.original_total || ''),
    [i.thickness, i.height, i.length].filter(v => v != null && v !== '').join(' '),
    i.notched ? 'notched notch' : '',
    (i.lumber || []).map(l => l ? `${l.thickness || ''} ${l.width || ''} ${l.length || ''} ${l.description || ''}` : '').join(' '),
    dateStrings(i.cut_date),
  ].join(' ').toLowerCase();

  const query = search.trim().toLowerCase();
  const tokens = query.split(/\s+/).filter(Boolean);
  let filtered = items;
  let isFuzzy = false;
  if (tokens.length > 0) {
    filtered = items.filter(i => { const h = haystack(i); return tokens.every(t => h.includes(t)); });
    if (filtered.length === 0) {
      isFuzzy = true;
      filtered = items.map(i => ({ i, s: fuzzyScore(query, haystack(i)) })).filter(({ s }) => s >= 0.45).sort((a, b) => b.s - a.s).map(({ i }) => i);
    }
  }
  const sorted = isFuzzy ? filtered : [...filtered].sort((a, b) => String(b.history_moved_at || b.cut_date || '').localeCompare(String(a.history_moved_at || a.cut_date || '')));

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto [&>button]:hidden" data-testid="cut-components-history-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>
              Components History{listName ? ` — ${listName}` : ''}
            </DialogTitle>
            <button onClick={() => onOpenChange(false)} className="p-1.5 -mt-1 -mr-1 text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 transition-colors flex-shrink-0" data-testid="components-history-close-x">
              <X className="w-6 h-6" />
            </button>
          </div>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search component, pallet type, date…"
            className="rounded-none border-zinc-300 h-9 text-sm"
            data-testid="list-cut-history-search" />
          {items.length === 0 ? (
            <p className="text-sm text-zinc-400 text-center py-6" data-testid="list-cut-history-empty">No components in history yet for this cut list. Use the 3-dots menu on a cut list component to move it here.</p>
          ) : sorted.length === 0 ? (
            <p className="text-sm text-zinc-400 text-center py-6" data-testid="list-cut-history-no-results">Nothing in history even close to &quot;{search}&quot;</p>
          ) : (
            <div className="space-y-3 max-h-[440px] overflow-y-auto pr-0.5">
              {isFuzzy && (
                <p className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 border border-amber-200">
                  No exact match — closest matches:
                </p>
              )}
              {sorted.map((i, hIdx) => (
                <div key={i.id} data-testid={`list-cut-hist-item-${i.id}`}>
                  <CutListItem
                    item={i} idx={hIdx} dragIdx={null} tick={0} reorderMode={false}
                    formatTime={fmtEst} formatStopwatch={fmtSw} getElapsed={getElapsed}
                    toggleCheck={noop} removeFromList={noop} moveItem={noop} toggleSavedForLater={noop}
                    updateItemCustomQty={noop} updateItemStopwatch={noop}
                    startTimer={noop} stopTimer={noop} resetTimer={noop}
                    editingIdx={null} setEditingIdx={noop} customQty="" setCustomQty={noop}
                    editingTimeId={null} setEditingTimeId={noop} customTime="" setCustomTime={noop}
                    readOnly={true} onDeleteHistory={() => setDeleteId(i.id)} onRestoreHistory={() => onRestore(i.id)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>

    <AlertDialog open={!!deleteId} onOpenChange={(o) => { if (!o) setDeleteId(null); }}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none" data-testid="delete-list-cut-hist-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Delete Component?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">Are you sure you want to permanently delete this component from your history? This cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => { onDelete(deleteId); setDeleteId(null); }} className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="confirm-delete-list-cut-hist-btn">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
